import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Box, Icon, Spinner, Text } from "@salestrak/ui";
import { useOptionalActivityContext } from "./activity-context";
import type {
  ActivityNode,
  ActivityDayGroup,
  ActivitySegment,
} from "./activity-types";
import { GROUP_THRESHOLD, SCROLL_THRESHOLD } from "./activity-types";
import { getDateLabel } from "./activity-utils";
import { formatDateKey } from "@salestrak/ui";
import { DateSeparator, DatePill, useDateJump } from "@salestrak/ui";
import { ActivityItem } from "./activity-item";
import type { Omittable } from "@salestrak/ui";

const SEGMENT_TOGGLE_LEFT_OFFSET = -4;

interface SegmentToggleProps {
  icon: "chevron-right" | "chevron-up";
  label: string;
  onClick: () => void;
}

function SegmentToggle({ icon, label, onClick }: SegmentToggleProps) {
  return (
    <Box
      alignSelf="start" as="button"
      direction="row"
      gap="sm"
      align="center"
      position="relative"
      left={SEGMENT_TOGGLE_LEFT_OFFSET}
      padding="xs"
      paddingRight="sm"
      rounded="sm"
      hoverBackground
      className="opacity-95"
      onClick={onClick}
    >
      <Box
        align="center"
        justify="center"
        width={20}
        height={20}
        rounded="sm"
        border
        background="muted"
      >
        <Icon name={icon} size="xs" color="muted" />
      </Box>
      <Text size="sm" variant="muted" weight="medium">
        {label}
      </Text>
    </Box>
  );
}

type ActivityListProps = Omittable<{
  issueId: string;
  noteId?: string | null;
  contentMaxWidth?: number;
}>;

/**
 * The issue activity feed — a bottom-anchored, day-grouped list over its own
 * overflow container, paginated by IntersectionObserver.
 */
export function ActivityList({ issueId, contentMaxWidth }: ActivityListProps) {
  const ctx = useOptionalActivityContext();
  const {
    activities = [],
    getNote,
    loadMoreActivities,
    hasMoreActivities,
    loadingMore,
    activityDates,
  } = ctx ?? {};

  const scrollRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<any>(null);
  const [collapsedSegments, setCollapsedSegments] = useState<
    Map<string, boolean>
  >(new Map());
  const [stickToBottom, setStickToBottom] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  // Synchronous gate — the observer callback can fire faster than React state
  // propagates, so we also block via a ref to prevent racing pages.
  const isLoadingPageRef = useRef(false);
  // Tracks the last scrollHeight that the stick-to-bottom effect has seen so
  // we only re-snap when content actually grows (new note arrives) rather
  // than on every unrelated re-render.
  const lastScrollHeightRef = useRef(0);

  // ═══════════════════════════════════════════════════════════════════════════
  // Activity grouping
  // ═══════════════════════════════════════════════════════════════════════════

  const isNote = useCallback(
    (activity: ActivityNode): boolean => {
      if (
        !activity.changeset?.note?.before &&
        activity.changeset?.note?.after
      )
        return true;
      if (activity.changeset?.age?.after?.noteId) return true;
      return false;
    },
    [],
  );

  // A callback, not a plain declaration: the grouping memo above calls it, and
  // a new identity every render would rebuild every segment every render.
  const createActivitySegment = useCallback(function createActivitySegment(
    segActivities: (ActivityNode & { isLast: boolean })[],
    dateKey: string,
    segIdx: number,
    isLastSegment: boolean,
    isLastDay: boolean,
  ): ActivitySegment {
    const key = `${dateKey}-${segIdx}`;
    const isOnlySegment = segIdx === 0 && isLastSegment;

    if (
      !collapsedSegments.has(key) &&
      segActivities.length > GROUP_THRESHOLD &&
      !isOnlySegment &&
      !(isLastSegment && isLastDay)
    ) {
      setCollapsedSegments((prev) => {
        const next = new Map(prev);
        next.set(key, true);
        return next;
      });
    }

    const isCollapsed = collapsedSegments.get(key) ?? false;

    return {
      type: "activities",
      activities: segActivities,
      collapsed: isCollapsed,
      segmentKey: key,
    };
  }, [collapsedSegments]);

  const activitiesByDay = useMemo((): ActivityDayGroup[] => {
    if (!Array.isArray(activities) || !activities.length) return [];

    const sortedActivities = [...activities].sort((a, b) => {
      const dateA = a?.lastUpdateDate
        ? new Date(a.lastUpdateDate).getTime()
        : 0;
      const dateB = b?.lastUpdateDate
        ? new Date(b.lastUpdateDate).getTime()
        : 0;
      return dateA - dateB;
    });

    const groups = new Map<
      string,
      {
        dateKey: string;
        dateLabel: string;
        timestamp: number;
        activities: ActivityNode[];
      }
    >();

    for (const activity of sortedActivities) {
      if (!activity?.creationDate) continue;

      const activityDate = new Date(activity.creationDate);
      activityDate.setHours(0, 0, 0, 0);
      // Local-day key shared with the inbox and the jump-to-date popover (see
      // date-jump/formatDateKey): a UTC key would disagree with the server's
      // local-zone keying and the popover's today/yesterday math.
      const dateKey = formatDateKey(activityDate);
      const dateLabel = getDateLabel(activityDate);

      if (!groups.has(dateKey)) {
        groups.set(dateKey, {
          dateKey,
          dateLabel,
          timestamp: activityDate.getTime(),
          activities: [],
        });
      }

      groups.get(dateKey)!.activities.push(activity);
    }

    const processedGroups = Array.from(groups.values()).map(
      (group, groupIndex, allGroups) => {
        const segments: ActivitySegment[] = [];
        let currentActivityGroup: (ActivityNode & { isLast: boolean })[] = [];
        let segmentIndex = 0;
        const isLastDay = groupIndex === allGroups.length - 1;

        // Filter out deleted notes and invalid age activities
        const validActivities = group.activities.filter((activity) => {
          if (isNote(activity)) {
            if (getNote) {
              const nId =
                activity.changeset?.note?.after ??
                activity.changeset?.age?.after?.noteId;
              const noteExists = getNote(nId);
              return noteExists !== null && noteExists !== undefined;
            }
          }
          return true;
        });

        for (const [i, activity] of validActivities.entries()) {
          const next = validActivities[i + 1];
          const isLastInGroup = next === undefined;
          const nextIsNote = next !== undefined && isNote(next);

          if (isNote(activity)) {
            if (currentActivityGroup.length > 0) {
              segments.push(
                createActivitySegment(
                  currentActivityGroup,
                  group.dateKey,
                  segmentIndex++,
                  false,
                  isLastDay,
                ),
              );
              currentActivityGroup = [];
            }
            segments.push({
              type: "note",
              activity,
              last: isLastInGroup || nextIsNote,
            });
          } else {
            currentActivityGroup.push({
              ...activity,
              isLast: isLastInGroup || nextIsNote,
            });
          }
        }

        if (currentActivityGroup.length > 0) {
          segments.push(
            createActivitySegment(
              currentActivityGroup,
              group.dateKey,
              segmentIndex,
              true,
              isLastDay,
            ),
          );
        }

        return { ...group, segments };
      },
    );

    return processedGroups.sort((a, b) => a.timestamp - b.timestamp);
  }, [activities, getNote, isNote, createActivitySegment]);


  const toggleSegment = useCallback((key: string) => {
    setCollapsedSegments((prev) => {
      const next = new Map(prev);
      next.set(key, !next.get(key));
      return next;
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // Jump-to-date + sticky date (shared with the inbox via useDateJump)
  // ═══════════════════════════════════════════════════════════════════════════

  const dateJumpGroups = useMemo(
    () =>
      activitiesByDay.map((g) => ({ dateKey: g.dateKey, dateLabel: g.dateLabel })),
    [activitiesByDay],
  );

  const { scrollToDate, stickyDateLabel, getScrollElement, isJumpingRef } =
    useDateJump({
      scrollRef,
      groups: dateJumpGroups,
      hasMore: hasMoreActivities ?? false,
      loadMore: loadMoreActivities,
      // Releasing stick-to-bottom and claiming the page-load gate keeps the
      // IntersectionObserver (which checks isLoadingPageRef) from firing its
      // own loadPage while the jump paginates toward the target day.
      onJumpStart: () => {
        setStickToBottom(false);
        isLoadingPageRef.current = true;
        setIsLoadingPage(true);
      },
      onJumpEnd: () => {
        isLoadingPageRef.current = false;
        setIsLoadingPage(false);
      },
    });

  // ═══════════════════════════════════════════════════════════════════════════
  // Scroll handling — the overflow Box above is the scroller itself
  // ═══════════════════════════════════════════════════════════════════════════

  const checkScrollPosition = useCallback(() => {
    const container = getScrollElement();
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom <= SCROLL_THRESHOLD;
    setStickToBottom(isAtBottom);
  }, [getScrollElement]);

  // Register the stick-to-bottom scroll listener. The sticky-date listener is
  // owned by useDateJump.
  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    el.addEventListener("scroll", checkScrollPosition, { passive: true });
    return () => el.removeEventListener("scroll", checkScrollPosition);
  }, [getScrollElement, checkScrollPosition]);

  // Stick-to-bottom: only snap when content actually grows. Triggering on
  // every render fights the user — they could not scroll up while still
  // within SCROLL_THRESHOLD of the bottom because the next unrelated render
  // (e.g. sticky-date label update) re-snapped them to the bottom.
  useEffect(() => {
    const el = getScrollElement();
    if (!el) return;
    const grew = el.scrollHeight > lastScrollHeightRef.current;
    lastScrollHeightRef.current = el.scrollHeight;
    if (grew && stickToBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activities.length, stickToBottom, getScrollElement]);

  const loadPage = useCallback(async () => {
    if (isLoadingPageRef.current || loadingMore) return;
    if (!loadMoreActivities || !hasMoreActivities) return;

    const container = getScrollElement();
    const scrollTopBefore = container?.scrollTop ?? 0;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const needsManualScrollPreserve = scrollTopBefore < SCROLL_THRESHOLD;

    isLoadingPageRef.current = true;
    setIsLoadingPage(true);
    try {
      await loadMoreActivities();

      // Wait for the new content to commit before flipping the gate so the
      // observer (which re-creates when isLoadingPage flips false) sees the
      // spacer pushed out of the rootMargin zone — otherwise it immediately
      // fires the next page.
      if (needsManualScrollPreserve && container) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => {
            const scrollHeightAfter = container.scrollHeight;
            const heightAdded = scrollHeightAfter - scrollHeightBefore;
            if (heightAdded > 0) {
              container.scrollTop = scrollTopBefore + heightAdded;
            }
            resolve();
          });
        });
      } else {
        // Even without scroll preservation, give the browser a frame to lay
        // out the new rows before the observer re-evaluates intersection.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      }
    } finally {
      isLoadingPageRef.current = false;
      setIsLoadingPage(false);
    }
  }, [loadingMore, loadMoreActivities, hasMoreActivities, getScrollElement]);

  // IntersectionObserver for infinite scroll. rootMargin pre-fires the load
  // ~800px before the spacer reaches the viewport so the spinner is visible
  // before the user actually hits the top.
  useEffect(() => {
    const spacer = spacerRef.current;
    const container = getScrollElement();
    if (!spacer || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            hasMoreActivities &&
            !isLoadingPageRef.current &&
            !isJumpingRef.current
          ) {
            loadPage();
          }
        });
      },
      { root: container, rootMargin: "800px 0px 0px 0px", threshold: 0 },
    );

    observer.observe(spacer);
    return () => observer.disconnect();
  }, [hasMoreActivities, isLoadingPage, getScrollElement, loadPage, isJumpingRef]);


  // ═══════════════════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <Box direction="column" flex={1} minHeight={0} position="relative">
      {/* Floating date pill — appears when a date separator has scrolled past
          the top. The wrapper spans the column so the pill centers, but only
          the pill itself takes the pointer. */}
      {stickyDateLabel && (
        <Box
          pointerEvents="none" position="absolute"
          top={8}
          left={0}
          right={0}
          zIndex={20}
          align="center"
        >
          <Box pointerEvents="auto">
            <DatePill
              label={stickyDateLabel}
              scrollToDate={scrollToDate}
              dateKeys={activityDates}
            />
          </Box>
        </Box>
      )}

      {/* Plain overflow container, not a ScrollArea: this feed OWNS its
          scroller (stick-to-bottom math, the pagination observer's root, and
          useDateJump's scroll target all read this element directly). */}
      <Box ref={scrollRef} fill="flex" overflow="auto">
        <Box direction="column" align="center">
          <Box
            direction="column"
            fill="width"
            maxWidth={contentMaxWidth}
            paddingX="md"
            paddingTop="sm"
            paddingBottom="md"
          >
            {/* Spacer pushes content to bottom + triggers infinite scroll */}
            <Box ref={spacerRef} fill="height">
              {loadingMore && (
                <Box align="center" padding="sm">
                  <Spinner size="xl" />
                </Box>
              )}
            </Box>

            {activitiesByDay.map((group, groupIndex) => (
              <Box
                key={`${group.dateKey}-${groupIndex}`}
                id={`date-group-${group.dateKey}`}
              >
                <DateSeparator
                  label={group.dateLabel}
                  scrollToDate={scrollToDate}
                  dateKeys={activityDates}
                />
                {group.segments.map((segment) => {
                  if (segment.type === "note") {
                    return (
                      <ActivityItem
                        key={`note-${segment.activity.activityId}`}
                        issueId={issueId}
                        activity={segment.activity}
                        last={segment.last}
                      />
                    );
                  }

                  if (segment.activities.length <= GROUP_THRESHOLD) {
                    return segment.activities.map((activity) => (
                      <ActivityItem
                        key={activity.activityId}
                        issueId={issueId}
                        activity={activity}
                        last={activity.isLast}
                      />
                    ));
                  }

                  return (
                    <Box key={segment.segmentKey} paddingBottom="md">
                      {segment.collapsed ? (
                        <SegmentToggle
                          icon="chevron-right"
                          label={`Show ${segment.activities.length} more`}
                          onClick={() => toggleSegment(segment.segmentKey)}
                        />
                      ) : (
                        <>
                          {/* The final row keeps its own `isLast`: forcing a
                              connector under it runs a dangling stub past the
                              group into the next date separator. */}
                          {segment.activities.map((activity) => (
                            <ActivityItem
                              key={activity.activityId}
                              issueId={issueId}
                              activity={activity}
                              last={activity.isLast}
                            />
                          ))}
                          <SegmentToggle
                            icon="chevron-up"
                            label="Hide"
                            onClick={() => toggleSegment(segment.segmentKey)}
                          />
                        </>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Bottom edge fade over the scroller — the feed's last rows dissolve
          into the panel instead of clipping. Same hand-rolled gradient the
          kanban column uses; the panel behind is `popover`. */}
      <Box
        aria-hidden
        pointerEvents="none"
        position="absolute"
        className="inset-x-0 bottom-0 h-6 bg-linear-to-t from-popover to-transparent"
      />
    </Box>
  );
}
